/**
 * evaluateClientRules — Client-side validation of displayed rows against
 * target-schema rules.  Produces per-cell violations with accurate row
 * indices that match the grid's current sort / pagination.
 */
import type { ColumnRules, SchemaRule } from './buildValidationSql';
import { canonicalRuleType, defaultSeverity } from './buildValidationSql';

export interface CellViolation {
  column: string;
  rowIndex: number;
  severity: 'error' | 'warning' | 'info';
  message: string;
  remediation?: string;
}

// ── Value helpers ────────────────────────────────────────────────────

function isNullOrEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function isParseableDate(value: unknown): boolean {
  if (value == null) return false;
  const s = String(value).trim();
  if (!s) return false;
  return !Number.isNaN(Date.parse(s));
}

// ── Per-rule checkers ────────────────────────────────────────────────
// Each returns `true` when the cell **violates** the rule.

type RuleChecker = (
  value: unknown,
  row: Record<string, unknown>,
  rule: SchemaRule,
) => boolean;

const RULE_CHECKERS: Record<string, RuleChecker> = {
  not_empty: (value) => isNullOrEmpty(value),

  enum: (value, _row, rule) => {
    if (value == null) return false; // nulls handled by nullable rule
    // Phase 3 typed shape emits ``values``; legacy recipes carried
    // ``allowed_values``. Read both transitionally.
    const allowed = Array.isArray(rule.values) && rule.values.length > 0
      ? rule.values
      : rule.allowed_values;
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    const set = new Set(allowed.map((v) => String(v).toLowerCase()));
    return !set.has(String(value).toLowerCase());
  },

  regex: (value, _row, rule) => {
    if (value == null) return false;
    const pattern = rule.pattern;
    if (!pattern) return false;
    try {
      return !new RegExp(pattern).test(String(value));
    } catch {
      return false; // invalid regex — skip
    }
  },

  length: (value, _row, rule) => {
    if (value == null) return false;
    const len = String(value).length;
    const minLen = rule.min_length ?? rule.min;
    const maxLen = rule.max_length ?? rule.max;
    if (minLen != null && len < Number(minLen)) return true;
    if (maxLen != null && len > Number(maxLen)) return true;
    return false;
  },

  range: (value, _row, rule) => {
    if (value == null) return false;
    const n = toNumber(value);
    if (n == null) return false; // non-numeric handled by numeric rule
    if (rule.min != null && n < Number(rule.min)) return true;
    if (rule.max != null && n > Number(rule.max)) return true;
    return false;
  },

  numeric: (value) => {
    if (value == null) return false;
    return toNumber(value) == null;
  },

  date_format: (value) => {
    if (value == null) return false;
    return !isParseableDate(value);
  },

  before_date: (value, row, rule) => {
    if (value == null) return false;
    const field = rule.field;
    if (!field) return false;
    const other = row[field];
    if (other == null) return false;
    const d1 = Date.parse(String(value));
    const d2 = Date.parse(String(other));
    if (Number.isNaN(d1) || Number.isNaN(d2)) return false;
    return d1 >= d2;
  },

  after_date: (value, row, rule) => {
    if (value == null) return false;
    const field = rule.field;
    if (!field) return false;
    const other = row[field];
    if (other == null) return false;
    const d1 = Date.parse(String(value));
    const d2 = Date.parse(String(other));
    if (Number.isNaN(d1) || Number.isNaN(d2)) return false;
    return d1 <= d2;
  },
};

// ── Primary key duplicate detection (two-pass) ──────────────────────

function findDuplicateIndices(
  rows: Record<string, unknown>[],
  column: string,
): number[] {
  const freq = new Map<string, number[]>();
  for (let i = 0; i < rows.length; i++) {
    const v = rows[i][column];
    if (v == null) continue;
    const key = String(v);
    const indices = freq.get(key);
    if (indices) indices.push(i);
    else freq.set(key, [i]);
  }
  const dupes: number[] = [];
  freq.forEach((indices) => {
    if (indices.length > 1) dupes.push(...indices);
  });
  return dupes;
}

// ── Public API ───────────────────────────────────────────────────────

export function evaluateCellViolations(
  rows: Record<string, unknown>[],
  columnRules: ColumnRules[],
): CellViolation[] {
  if (!rows.length || !columnRules.length) return [];

  const violations: CellViolation[] = [];

  for (const { columnName, rules, isNullable, isPrimaryKey } of columnRules) {
    // Structural: nullable check
    if (isNullable === false) {
      for (let i = 0; i < rows.length; i++) {
        if (isNullOrEmpty(rows[i][columnName])) {
          violations.push({
            column: columnName,
            rowIndex: i,
            severity: 'error',
            message: `${columnName} must not be null or empty`,
          });
        }
      }
    }

    // Structural: primary key uniqueness
    if (isPrimaryKey) {
      const dupeIndices = findDuplicateIndices(rows, columnName);
      for (const idx of dupeIndices) {
        violations.push({
          column: columnName,
          rowIndex: idx,
          severity: 'error',
          message: `Duplicate primary key value in ${columnName}`,
        });
      }
    }

    // Explicit validation_rules
    for (const rule of rules) {
      const ruleType = canonicalRuleType(rule);
      if (!ruleType || ruleType === 'custom') continue;

      const checker = RULE_CHECKERS[ruleType];
      if (!checker) continue;

      const severity = defaultSeverity(ruleType, rule) as 'error' | 'warning' | 'info';
      const msg = rule.message || `Violates ${ruleType} rule on ${columnName}`;
      const remediation = ruleType === 'enum'
        ? 'Value not in allowed list'
        : undefined;

      for (let i = 0; i < rows.length; i++) {
        if (checker(rows[i][columnName], rows[i] as Record<string, unknown>, rule)) {
          violations.push({
            column: columnName,
            rowIndex: i,
            severity,
            message: msg,
            remediation,
          });
        }
      }
    }
  }

  return violations;
}

// ── Severity ranking helper ──────────────────────────────────────────

export function severityRank(severity: string): number {
  if (severity === 'error') return 2;
  if (severity === 'warning') return 1;
  return 0;
}
